import { Icon, IconButton, SearchableSelect, Select } from '@/components/ui';
import { cn } from '@/utils/cn';

import type {
  Condition,
  ConditionOperator,
  ConditionTree,
} from '@/api/queries/workspace';

export interface ConditionField {
  value: string;
  label: string;
  /** Operators allowed for this field. Defaults to a sensible string set. */
  operators?: ConditionOperator[];
  /** Set when the value should render as a fixed-value select instead of a free input. */
  options?: { value: string; label: string }[];
  /**
   * When true, the value input becomes a searchable combobox over the
   * `contact.{value}` counter table (free text still allowed). Only takes effect
   * for single-value operators — see SINGLE_VALUE_OPS.
   */
  counter?: boolean;
}

/** Operators whose value is a single scalar, so a counter combobox can drive it.
 *  `in`/`not_in`/`between` carry comma/range lists and keep the plain text input. */
const SINGLE_VALUE_OPS = new Set<ConditionOperator>([
  'equals',
  'not_equals',
  'contains',
  'starts_with',
  'ends_with',
]);

const OPERATOR_LABELS: Record<ConditionOperator, string> = {
  equals: 'is',
  not_equals: 'is not',
  contains: 'contains',
  starts_with: 'starts with',
  ends_with: 'ends with',
  in: 'is one of',
  not_in: 'is not one of',
  gt: 'greater than',
  lt: 'less than',
  between: 'between',
  before: 'before',
  after: 'after',
  is_set: 'is set',
  is_empty: 'is empty',
};

const DEFAULT_OPERATORS: ConditionOperator[] = [
  'equals',
  'not_equals',
  'contains',
  'starts_with',
  'is_set',
  'is_empty',
];

interface ConditionBuilderProps {
  value: ConditionTree;
  onChange: (next: ConditionTree) => void;
  fields: ConditionField[];
  /**
   * Hide the conjunction (AND/OR) toggle when there's only ever one row,
   * e.g. when used inline for a single tag auto-rule. Defaults to false.
   */
  hideConjunctionWithSingleRow?: boolean;
  /**
   * Feature-layer hook returning value suggestions for a counter-backed field
   * (called once per row). Keeps this component free of `@/api` deps — the
   * contacts/segments features inject an adapter over useContactFieldSuggestions.
   * Given `undefined` for non-counter fields, must return no suggestions.
   */
  useValueSuggestions?: (counterPath?: string) => string[];
}

export function ConditionBuilder({
  value,
  onChange,
  fields,
  hideConjunctionWithSingleRow,
  useValueSuggestions,
}: ConditionBuilderProps) {
  const conditions = value.conditions ?? [];
  const showConjunction = !(hideConjunctionWithSingleRow && conditions.length <= 1);

  const setConjunction = (conjunction: 'and' | 'or') => onChange({ ...value, conjunction });

  const updateRow = (idx: number, next: Partial<Condition>) => {
    const updated = conditions.map((c, i) => (i === idx ? { ...c, ...next } : c));
    onChange({ ...value, conditions: updated });
  };

  const addRow = () => {
    const first = fields[0];
    // Default to the field's first allowed operator (e.g. "contains" for Name)
    // rather than always "equals", which silently required an exact match.
    const operator = first?.operators?.[0] ?? 'equals';
    onChange({
      ...value,
      conditions: [
        ...conditions,
        { field: first?.value ?? '', operator, value: '' },
      ],
    });
  };

  // When the field changes, keep the current operator only if the new field still
  // allows it; otherwise fall back to its first operator. Prevents the operator
  // select from showing a value that isn't in its option list.
  const changeField = (idx: number, nextField: string) => {
    const nf = fields.find((f) => f.value === nextField);
    const ops = nf?.operators ?? DEFAULT_OPERATORS;
    const current = conditions[idx]?.operator;
    const operator = current && ops.includes(current) ? current : ops[0];
    updateRow(idx, { field: nextField, operator });
  };

  const removeRow = (idx: number) => {
    onChange({ ...value, conditions: conditions.filter((_, i) => i !== idx) });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {showConjunction && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 11, color: 'var(--text-3)' }}>Match</span>
          <div className="cb-conjunction" role="radiogroup" aria-label="Combine conditions">
            <button
              type="button"
              role="radio"
              aria-checked={value.conjunction === 'and'}
              className={cn(value.conjunction === 'and' && 'active')}
              onClick={() => setConjunction('and')}
            >
              ALL
            </button>
            <button
              type="button"
              role="radio"
              aria-checked={value.conjunction === 'or'}
              className={cn(value.conjunction === 'or' && 'active')}
              onClick={() => setConjunction('or')}
            >
              ANY
            </button>
          </div>
          <span style={{ fontSize: 11, color: 'var(--text-3)' }}>
            of the following conditions
          </span>
        </div>
      )}

      <div className="cb-group">
        {conditions.length === 0 ? (
          <div style={{ fontSize: 12, color: 'var(--text-3)', padding: '4px 2px' }}>
            No conditions yet — every contact will match.
          </div>
        ) : (
          conditions.map((cond, idx) => (
            <ConditionRow
              key={idx}
              cond={cond}
              fields={fields}
              useValueSuggestions={useValueSuggestions}
              onChangeField={(next) => changeField(idx, next)}
              onUpdate={(next) => updateRow(idx, next)}
              onRemove={() => removeRow(idx)}
            />
          ))
        )}
        <button
          type="button"
          className="ds-btn ds-btn-ghost ds-btn-sm"
          style={{ alignSelf: 'flex-start' }}
          onClick={addRow}
        >
          <Icon name="plus" size={12} />
          Add condition
        </button>
      </div>
    </div>
  );
}

interface ConditionRowProps {
  cond: Condition;
  fields: ConditionField[];
  useValueSuggestions?: (counterPath?: string) => string[];
  onChangeField: (next: string) => void;
  onUpdate: (next: Partial<Condition>) => void;
  onRemove: () => void;
}

function ConditionRow({
  cond,
  fields,
  useValueSuggestions,
  onChangeField,
  onUpdate,
  onRemove,
}: ConditionRowProps) {
  const field = fields.find((f) => f.value === cond.field);
  const operators = field?.operators ?? DEFAULT_OPERATORS;
  const valuelessOperator = cond.operator === 'is_set' || cond.operator === 'is_empty';

  // One suggestions lookup per row. Non-counter fields pass undefined, so the
  // adapter returns nothing and issues no request.
  const counterPath = field?.counter ? field.value : undefined;
  const suggestions = useValueSuggestions?.(counterPath) ?? [];
  const useValueCombo =
    !!counterPath && suggestions.length > 0 && SINGLE_VALUE_OPS.has(cond.operator);

  return (
    <div className="cb-row">
      <SearchableSelect
        className="cb-select"
        ariaLabel="Field"
        value={cond.field}
        onChange={onChangeField}
        options={fields.map((f) => ({ value: f.value, label: f.label }))}
      />
      <Select
        className="cb-select"
        value={cond.operator}
        onChange={(e) => onUpdate({ operator: e.target.value as ConditionOperator })}
        options={operators.map((op) => ({ value: op, label: OPERATOR_LABELS[op] }))}
      />
      {valuelessOperator ? (
        <span />
      ) : field?.options ? (
        <Select
          className="cb-select"
          value={typeof cond.value === 'string' ? cond.value : ''}
          onChange={(e) => onUpdate({ value: e.target.value })}
          options={[{ value: '', label: '—' }, ...field.options]}
        />
      ) : useValueCombo ? (
        <SearchableSelect
          freeText
          className="cb-select"
          ariaLabel="Value"
          placeholder="Value"
          value={
            cond.value === undefined || cond.value === null ? '' : String(cond.value)
          }
          onChange={(v) => onUpdate({ value: v })}
          options={suggestions.map((s) => ({ value: s, label: s }))}
        />
      ) : (
        <input
          className="cb-input"
          placeholder="Value"
          value={
            cond.value === undefined || cond.value === null
              ? ''
              : Array.isArray(cond.value)
              ? cond.value.join(', ')
              : String(cond.value)
          }
          onChange={(e) => onUpdate({ value: e.target.value })}
        />
      )}
      <IconButton
        icon="x"
        iconSize={12}
        size="sm"
        label="Remove condition"
        onClick={onRemove}
      />
    </div>
  );
}
